<template>
    <div>
        <overlay :active="isLoading"/>
        <div class="row page-title">
            <div class="col-md-12">
                <h1>Campaign Manager</h1>
            </div>
        </div>

        <campaign-manager-details :campaign-data="campaign">
            <ul class="campaign-details__summary list-unstyled" v-cloak>
                <template v-if="campaign.blnBankrateSelect">
                    <li class="campaign-details__option">
                        <span>{{ fundsPoolBalance | currency }}</span>
                        <span class="list-header">Campaign Balance</span>
                    </li>
                </template>
            </ul>
        </campaign-manager-details>

        <div v-if="canShowBillingLinks" class="campaign-box campaign-box--no_border">
            <a :href="`/admin/credits-manager/credits?advertiser_id=${campaign.advertiser_id}&funds_pool_id=${campaign.funds_pool_id}`" target="_blank" class="p-r-lg">Manage Credits</a>
            <a :href="`/admin/funds-manager/funds?advertiser_id=${campaign.advertiser_id}&funds_pool_id=${campaign.funds_pool_id}`" target="_blank">Manage Funds</a>
        </div>

        <div class="row" v-if="embedUrl">
            <div class="col-md-12">
                <div class="campaign-box campaign-box--no_border">
                    <div class="campaign-details-subtitle">Campaign Metrics</div>

                    <div>
                        <div>
                            <p>View your amount spent across all campaigns for the selected date range.</p>
                        </div>

                        <span class="datepicker-label">Date range</span>

                        <div class="row">
                            <div class="col-sm-4">
                                <looker-date-range-picker
                                    v-model="dateRange"
                                ></looker-date-range-picker>
                            </div>

                            <div class="col-sm-2 col-sm-offset-6 text-right" v-if="embedUrl">
                                <a @click.prevent="toggleLookerChart()" href="#" type="button" class="btn --gray" style="margin-top: 5px;">
                                    {{ showLookerChart ? 'Hide' : 'Show' }} Graph
                                </a>
                            </div>
                        </div>

                        <looker-embed v-if="embedUrl" v-show="showLookerChart"
                                      height="740px"
                                      :embed-url="embedUrl"
                                      :date-range-string="dateRangeString"
                        ></looker-embed>
                    </div>
                </div>
            </div>
        </div>

        <campaign-manager-ad-set-list
            :create-ad-set-url="createAdSetUrl"
            :campaign-id="campaign.id"
            :campaign="campaign"
            :loading="loadingLooker"
            :looker-loaded="lookerLoaded"
            :dimensions="dimensions"
            :vertical="vertical"
            :looker-ad-set-data="lookerAdSetData"
            :feature-flags="featureFlags"
        ></campaign-manager-ad-set-list>

        <div class="col-xs-12 m-b-md p-r-none">
            <a v-on:click.prevent="deleteCampaign()" v-if="canDeleteCampaign()" class="btn --red pull-right">
                <span aria-hidden="true"></span> Delete
            </a>
        </div>
    </div>
</template>

<script>
    import store from './store'
    import dataDog from '../../../mixins/datadog'
    import campaignCommon from 'js/campaigns/common'

    export default {
        mixins: [ dataDog, campaignCommon ],

        props: [
            'campaign',
            'embedUrl',
            'vertical',
            'dimensions',
            'adSets',
            'adSetDailySpends',
            'adSetDailyClickCounts',
            'budgetsIsOpen',
            'budgetsTypeLabel',
            'budgetsTotal',
            'budgetsIsWeekly',
            'budgetsTodaysBudget',
            'createAdSetUrl',
            'fundsPoolBalance',
            'featureFlags'
        ],

        data() {
            return {
                loadingLookerAdSets: true,
                lookerLoaded:        false, // track looker request failures
                lookerAdSets:        null,
                loadingEmbed:        true,
                dateRange:           null,
                adsetLookerData:     [],
                showLookerChart:     false
            }
        },

        computed: {
            isLoading: function () {
                return store.getters.isLoading;
            },
            dateRangeString() {
                if (!this.dateRange) return null

                const adjustedEndDate = this.dateRange.end.clone().add(1, 'd')

                return `${this.dateRange.start.format('YYYY/MM/DD')} to ${adjustedEndDate.format('YYYY/MM/DD')}`
            },

            loadingLooker() {
                return this.loadingLookerAdSets
            },

            lookerAdSetData() {
                if (this.loadingLookerAdSets || !this.embedUrl) {
                    return []
                }

                if (!this.lookerAdSets) {
                    return []
                }

                const { today, range } = this.lookerAdSets

                return _.map(range, (adSet) => {
                    const todayMatch = _.find(today, { adset_id: adSet.adset_id }) || {}
                    return {
                        adSetId:     adSet.adset_id,
                        today:       _.pick(todayMatch, ['clicks', 'revenue', 'position']),
                        inRange:     _.pick(adSet, ['clicks', 'revenue', 'position']),
                        daily_spend: this.adSetDailySpend(adSet.adset_id),
                        daily_count: this.adSetDailyClickCount(adSet.adset_id),
                    }
                })
            },

            hasLookerInformation() {
                return this.lookerAdSetData.length > 0
            },

            canShowBillingLinks () {
                return this.hasRole('engineer') || (this.hasRole('staff') && [10454, 11719].includes(this.campaign.advertiser_id))
            }
        },

        watch: {
            dateRange() {
                this.updateDateRange()
                this.refreshAdsetDataFromLooker()
            }
        },

        methods: {
            updateDateRange() {
                store.commit('assignChart', {
                    startDate:       this.dateRange.start.format('YYYY-MM-DD'),
                    endDate:         this.dateRange.end.format('YYYY-MM-DD'),
                    isChartUpdating: true,
                })
            },

            refreshAdsetDataFromLooker() {
                Promise.all([
                    this.$http.get(route('api.looker.clicks-spend-position-today', {
                        'vertical':    this.vertical,
                        'campaign_id': this.campaign.id
                    })),
                    this.$http.get(route('api.looker.clicks-spend-position-in-range', {
                        'msg_date_date': this.dateRangeString,
                        'vertical':      this.vertical,
                        'campaign_id':   this.campaign.id
                    }))
                ])
                    .then(response => {
                        this.lookerAdSets = {
                            today: response[0].body.adsets,
                            range: response[1].body.adsets
                        }
                        this.loadingLookerAdSets = false
                        this.lookerLoaded = true
                    })
                    .catch(() => {
                        this.loadingLookerAdSets = false
                        this.lookerLoaded = false
                    })
            },

            adSetDailySpend(id) {
                return (Number(this.adSetDailySpends[id]) || 0) / 100
            },

            adSetDailyClickCount(id) {
                return Number(this.adSetDailyClickCounts[id])
            },

            toggleLookerChart() {
                this.showLookerChart = !this.showLookerChart
                this.dataDogIncrement((this.showLookerChart ? 'show' : 'hide') + '_looker_chart_manager')
                localStorage.setItem('show-looker-chart-manager', this.showLookerChart)
            },

            deleteCampaign() {
                this.showConfirmMessage(null, () => {
                    this.$http.delete(route('campaigns.delete', this.campaign.id)).then(response => {
                        if (response.data.success) {
                            this.redirect(route(`campaigns.${this.campaign.product_category.name.toLowerCase().replace(' ', '-')}.index`));
                        }
                    });
                });
            },

            canDeleteCampaign: function() {
                if (this.campaign.is_advertiser_edit_blocked && !this.hasRole(['staff', 'engineer'])) {
                    return false
                }

                return this.canEditWithSelect(this.campaign.blnBankrateSelect)
            }
        },

        created() {
            this.showLookerChart = localStorage.getItem('show-looker-chart-manager') === 'true'
        },

        mounted() {
            this.refreshAdsetDataFromLooker()
            store.commit('assignAdSets', this.adSets);
        }
    }
</script>
